<script setup lang="ts">
import { CtIcon, CtPill } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { formattedOfferPrice } from '~/composables/offer-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useOfferStore } from '~/stores/offer'
import { useReferralStore } from '~/stores/referral'

const referralStore = useReferralStore()
const metaFlowOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED && referralStore.isMetaFlow

const {
  selectedOffer,
  getCurrentBuildSeriesTranslation,
  getOfferDescription,
} = storeToRefs(useOfferStore())

const formattedPrice = computed<string | undefined>(() => selectedOffer.value
  ? formattedOfferPrice(selectedOffer.value, useTranslations().language.value)
  : undefined,
)

const packageInclusiveDistance = computed(() => {
  return selectedOffer.value && (selectedOffer.value.properties || []).find(prop => (prop.tags || []).includes('package_inclusive_distance'))
})

const distancePrice = computed(() => {
  return selectedOffer.value && (selectedOffer.value.properties || []).find(prop => (prop.tags || []).includes('distance_price'))
})
</script>

<template>
  <section class="p-16 flex flex-col border-b-8 border-b-cotton">
    <h4 class="text-lg text-black-100 flex items-center">
      <CtIcon name="car" color="--ct-color-black-100" size="40" class="flex-none mr-8" />
      <span class="text-md">{{ translate('sidebar_vehicle_title') }}</span>
    </h4>

    <header class="flex justify-between items-center">
      <div class="ml-8 basis-7/12">
        <p class="text-black-100">
          {{ getCurrentBuildSeriesTranslation }}
        </p>
        <p class="text-xs leading-normal text-black-80">
          {{ translate('car_or_similar_prebook') }}
        </p>

      </div>


      <ul class="text-right flex flex-col">
        <li class="text-black-100">
          {{ formattedPrice }}
        </li>
        <li class="text-xs leading-normal mt-2 text-black-80">
          <template v-if="selectedOffer?.mileageIncluded">
            {{ translate('mileage_included') }}
          </template>
          <template v-else-if="metaFlowOffersEnabled && packageInclusiveDistance?.value">
            {{
              translate('sidebar_included_kilometers', {
                '{package_inclusive_distance}': packageInclusiveDistance.value,
              })
            }}
          </template>
          <template v-else-if="!selectedOffer?.mileageIncluded">
            {{ useOfferStore().getFormattedPricePerDay }}/{{ translate('amount_per_day') }}
          </template>
        </li>
        <li class="text-xs leading-normal text-black-80">
          <template v-if="metaFlowOffersEnabled && distancePrice?.value && !selectedOffer?.mileageIncluded">
            {{
              translate('sidebar_distance_price', {
                '{distance_price}': distancePrice.value,
              })
            }}
          </template>
          <template v-else-if="!selectedOffer?.mileageIncluded">
            {{ getOfferDescription }}
          </template>
        </li>
      </ul>
    </header>

    <div v-if="selectedOffer?.fuelIncluded" class="flex justify-between items-center mt-8">
      <div class="ml-8">
        <p class="text-black-100">{{ translate('fuel_cost_included_footer') }}</p>
        <p class="text-xs leading-normal text-black-80">{{ translate('fuel_cost_covered_booking') }}</p>
      </div>
      <CtPill variant="success">{{ translate('free') }}</CtPill>
    </div>
  </section>
</template>
